import { useQueryClient } from '@tanstack/react-query'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUserCollections } from '@/hooks/api/useUsers'
import { useProduct } from '@/hooks/api/useProducts'
import { useCollectionToggle } from '@/hooks/useCollectionToggle'
import type { NormalizedCollection } from '@wtf/shared'
import { CollectionListItem } from './CollectionListItem'

interface CollectionListProps {
    productId: string
    searchQuery: string
    onCollectionToggle: (collectionId: string) => void
}

export function CollectionList({
    productId,
    searchQuery,
    onCollectionToggle,
}: CollectionListProps) {
    const { user } = useCurrentUser()
    const queryClient = useQueryClient()
    const { data: collectionIds = [], isLoading: collectionsLoading } =
        useUserCollections(user?.uid)
    const { data: productData } = useProduct(productId)
    const { isProductInCollection } = useCollectionToggle(productData)

    const filteredIds = (collectionIds || []).filter((cid) => {
        const col = queryClient.getQueryData<NormalizedCollection>([
            'collection',
            cid,
        ])
        if (!col || col.isArchived) return false
        const name = col?.name?.toLowerCase() || ''
        return name.includes(searchQuery.toLowerCase())
    })

    if (collectionsLoading) {
        return (
            <div className="py-4 text-center text-sm text-gray-500">
                Loading...
            </div>
        )
    }

    if (filteredIds.length === 0) {
        return (
            <div className="py-4 text-center text-sm text-gray-500">
                {searchQuery ? 'No collections found' : 'No collections'}
            </div>
        )
    }

    return (
        <div className="py-2">
            {filteredIds.map((cid) => {
                const collection =
                    queryClient.getQueryData<NormalizedCollection>(
                        ['collection', cid]
                    )
                if (!collection) return null

                return (
                    <CollectionListItem
                        key={cid}
                        collection={collection}
                        onToggle={onCollectionToggle}
                        isProductInCollection={isProductInCollection(cid)}
                    />
                )
            })}
        </div>
    )
}
